import React, { useEffect, useRef, useState } from 'react';
import { omit } from 'ramda';
import { throttle } from 'underscore';

import { Button } from '@interlayer/js/components/common/Button';
import { SvgIcon } from '@interlayer/js/app/components/common/SvgIcon';

import { TTubProps } from './types';
import { clsx } from '@interlayer/js/app/utils/clsx.util';
import { useNavigate } from '@interlayer/js/hooks/navigate';
import { isExternalUrl } from '@interlayer/js/utils/url.util';
import { storageService } from '@core/js/services/storage.service';
import {
    CLIPPING_DIRECTION,
    REDIRECT_FROM_TABS, SHOW_NOTIFICATION,
    TAB_NOTIFICATIONS_STATUS,
    TIME_FOR_HIDING_NOTIFICATION
} from '@interlayer/js/components/tagTypes/buzz/TopMenu/constants';
import { useSetQuery } from '@interlayer/js/app/hooks/query/useSetQuery';
import { useQueryObject } from '@interlayer/js/app/hooks/query/useQueryObject';
import { useInDesktop } from '@interlayer/js/app/hooks/device/hooks/useMediaQuery';
import { getClippingDirection } from '@interlayer/js/components/tagTypes/buzz/TopMenu/helpers/getClippingDirection';
import { DOWNLOAD_APP_LINK, STATE_DOWNLOAD_APP_PROMPT } from '@interlayer/js/app/components/main/DownloadApp/constants';

import style from './Tab.scss';

export const Tab: React.FC<TTubProps> = ({ amountOfElements, icon, link, notification, title, isActive, customStyle = {} }) => {
    const isDesktop = useInDesktop();
    const navigate = useNavigate();
    const params = useQueryObject();
    const setQuery = useSetQuery();
    const notificationRef = useRef<HTMLDivElement>(null);
    const [isShowNotification, setIsNotifications] = useState(false);
    const [clippingDirection, setClippingDirection] = useState<CLIPPING_DIRECTION>(CLIPPING_DIRECTION.unknown);
    const { backgroundColor, iconColor, activeTabBackgroundColor, bottomBorderColor } = customStyle;

    useEffect(() => {
        if (!notification || !notificationRef) {
            return;
        }

        const throttledCheckClippingDirection = throttle((event?: UIEvent) => {
            if (event?.type === 'resize') {
                setClippingDirection(CLIPPING_DIRECTION.unknown);
            }

            setClippingDirection(getClippingDirection(notificationRef.current));
        }, 300);

        throttledCheckClippingDirection();

        window.addEventListener('resize', throttledCheckClippingDirection);

        return () => {
            window.removeEventListener('resize', throttledCheckClippingDirection);
        };
    }, [isShowNotification, notification, notificationRef]);

    useEffect(() => {
        if (!isActive || !params?.[SHOW_NOTIFICATION]) {
            return;
        }

        const notificationsStatus = storageService.local.getItem(TAB_NOTIFICATIONS_STATUS) || {};
        setQuery(omit([SHOW_NOTIFICATION], params));

        if (notificationsStatus[link] || !notification) {
            return;
        }

        setIsNotifications(true);
        storageService.local.setItem(TAB_NOTIFICATIONS_STATUS, {
            ...notificationsStatus,
            [link]: true
        });

        setTimeout(() => {
            setIsNotifications(false);
        }, TIME_FOR_HIDING_NOTIFICATION);
    }, [isActive, link, notification, params, setQuery]);

    const handleClosePopup = () => {
        setIsNotifications(false);
    };

    const isAddQueryParams = storageService.local.getItem(DOWNLOAD_APP_LINK) !== STATE_DOWNLOAD_APP_PROMPT.NEED_TO_SHOW
        && !isDesktop
        && isExternalUrl(link);

    const handleNotifications = () => {
        const notificationsStatus = storageService.local.getItem(TAB_NOTIFICATIONS_STATUS) || {};
        let url = link;

        if (!notificationsStatus[link] || isAddQueryParams) {
            url += `?${new URLSearchParams({
                ...isAddQueryParams && { [REDIRECT_FROM_TABS]: '1' },
                ...!notificationsStatus[link] && (Boolean(notification) || isExternalUrl(link)) && { [SHOW_NOTIFICATION]: '1' }
            }).toString()}`;
        }

        if (isExternalUrl(link)) {
            storageService.local.setItem(TAB_NOTIFICATIONS_STATUS, {
                ...notificationsStatus,
                [link]: true
            });
        }

        navigate(url);
    };

    return (
        <div
            data-testid="Tab_item"
            key={link}
            style={{
                backgroundColor: isActive ? activeTabBackgroundColor : backgroundColor,
                ...isActive && bottomBorderColor && { borderBottom: `1px solid ${bottomBorderColor}` }
            }}
            className={
                clsx(
                    style.tab,
                    style[`tabSize_${amountOfElements}`],
                    { [style.isActive]: isActive },
                    { [style.isShowNotifications]: isShowNotification }
                )
            }
        >
            <Button
                data-testid="Tab_link"
                className={style.link}
                view="link"
                onClick={handleNotifications}
                style={{
                    color: iconColor
                }}
            >
                {title
                    ? (
                        <div dangerouslySetInnerHTML={{ __html: title }} />
                    )
                    : (
                        <SvgIcon
                            type={icon}
                            width="100%"
                            height="100%"
                            className={style.icon}
                        />
                    )}
            </Button>

            {Boolean(notification) && isShowNotification && (
                <div data-testid="Tab_notification" ref={notificationRef} className={clsx(style.notification, style[clippingDirection])}>
                    <div onClick={() => handleClosePopup()} className={style.closeBtn}>
                        <SvgIcon
                            width="0.55rem"
                            height="0.55rem"
                            className={style.closeIcon}
                            type="close-popup"
                        />
                    </div>

                    <div dangerouslySetInnerHTML={{ __html: notification }} />
                </div>
            )}
        </div>
    );
};
